import tinyColor from 'tinycolor2';

import config from 'configs';

const dark = '#000000';
const light = '#ffffff';

const getStyleElement = () => {
  const existingStyle = document.getElementById('css-variables');

  if (existingStyle) {
    return existingStyle;
  } else {
    const style = document.createElement('style');
    style.type = 'text/css';
    style.id = 'css-variables';
    return style;
  }
};

const appendColors = (colors, container) => {
  const style = getStyleElement();

  const cssSelector = Object.entries(colors).reduce(
    (css, [key, value], index, arr) =>
      css.concat(`  ${key}: ${value};\n${index === arr.length - 1 ? '}\n' : ''}`),
    `:root {\n`,
  );

  if (style.styleSheet) {
    // This is required for IE8 and below.
    style.styleSheet.cssText = cssSelector;
  } else {
    // Need to remove all children and a append new one
    style.innerHTML = '';
    style.appendChild(document.createTextNode(cssSelector));
  }

  container.append(style);
};

const lightness = color => parseInt(tinyColor(color).toHsl().l * 100);

const getRgbValue = color => {
  const { r, g, b } = tinyColor(color).toRgb();
  return `${r}, ${g}, ${b}`;
};

const getColorMode = (color, rgb = false) => {
  if (rgb) {
    return lightness(color) > 45 ? getRgbValue(dark) : getRgbValue(light);
  }
  return lightness(color) > 45 ? dark : light;
};

const setLightness = (color, amount) =>
  lightness(color) > 50
    ? tinyColor(color).darken(amount).toString()
    : tinyColor(color).lighten(amount).toString();

const getSkinMode = (color, rgb = false) => {
  if (rgb) {
    return lightness(color) > 14 ? getRgbValue(dark) : getRgbValue(light);
  }
  return lightness(color) > 14 ? dark : light;
};

const createSchemaClassic = colorList => {
  const {
    skin: skinColor,
    primary: primaryColor,
    secondary: secondaryColor,
    header: headerColor,
    text: textColor,
    bannerText: bannerTextColor,
    footer: footerColor,
    tabsBg: tabsBgColor,
    faqBg: faqBgColor,
    faqText: faqTextColor,
    faqBorder: faqBorderColor,
    faqTextActive: faqTextColorActive,
    faqGradientFrom: faqGradientFromColor,
    faqGradientTo: faqGradientToColor,
    burgerMenu: burgerMenuColor,
    activeTab: activeTabColor,
    tabBorder: tabBorderColor,
    navigationMenuBgColor: navMenuBgColor,
    navigationMenuSubListBgColor: navMenuSubListBgColor,
    walletConnectIcon: walletConnectIconColor,
    walletConnectBg: walletConnectBgColor,
    walletConnectBgHover: walletConnectBgHoverColor,
    walletConnectText: walletConnectTextColor,
    specificButtonText: specificButtonTextColor,
    specificButtonBg1: specificButtonBgColor1,
    specificButtonBg2: specificButtonBgColor2,
    accent: accentColor = '#8AD0F1',
    info: infoColor = '#485974',
    danger: dangerColor = '#AD2626',
    success: successColor = '#2E5C2A',
    warning: warningColor = '#FFB900',

    ...otherColors
  } = colorList;

  const skinMode = getColorMode(getColorMode(skinColor));
  const main = tinyColor(skinColor).toString();
  const changedColor = main === '#151517' ? '#0e0e0e' : main === '#ffffff' ? '#fafafa' : main;

  const mainColor =
    lightness(changedColor) > 45
      ? tinyColor(changedColor).desaturate(17.37).darken(4.31).toString()
      : tinyColor(changedColor).desaturate(17.37).lighten(4.31).toString();

  const getInvert = (name, value) =>
    otherColors[`${name.slice(2)}Invert`]
      ? getColorMode(otherColors[`${name.slice(2)}Invert`])
      : value;

  const skinSc = getColorMode(getInvert(`skinSc`, skinColor));
  const mainSc = getColorMode(getInvert(`mainSc`, mainColor));
  const primarySc = getColorMode(getInvert(`primarySc`, primaryColor));

  const rgbaZeroPointOne = '1a';
  const rgbaZeroPointTwo = '33';

  const colors = {
    '--skin': skinColor,
    '--mode': skinMode,
    '--main': mainColor,
    '--accent': accentColor,
    '--primary': primaryColor,
    '--secondary': secondaryColor || primaryColor,
    '--header-bgColor': headerColor || skinColor,
    '--text': textColor || getColorMode(skinColor),
    '--banner-text-color': bannerTextColor || textColor,
    '--tab-border-color': tabBorderColor || faqBorderColor || primaryColor,
    '--tabs-color': tabsBgColor || getColorMode(getInvert(`--skinSc`, skinColor)),
    '--active-tab-bgColor': tabsBgColor || 'transparent',
    '--active-tab-color': activeTabColor || primaryColor,
    '--nav-menu-bgColor': navMenuBgColor || mainColor,
    '--nav-menu-sub-list-bgColor': navMenuSubListBgColor || skinColor,
    '--footer-bgColor': footerColor || mainColor,
    '--gradient-from': faqGradientFromColor || primaryColor,
    '--specific-button-text-color': primarySc || primaryColor,
    '--specific-button-bg1': specificButtonBgColor1 || primaryColor,
    '--specific-button-bg2': specificButtonBgColor2 || secondaryColor,
    '--gradient-to': faqGradientToColor || secondaryColor,
    '--faq-bgColor': faqBgColor || mainColor,
    '--faq-text-color': faqTextColor || mainSc,
    '--faq-border-color': faqBorderColor || primaryColor,
    '--faq-text-color-active': faqTextColorActive || mainSc,
    '--burger-menu-color': burgerMenuColor || skinSc,
    '--wallet-connect-icon': walletConnectIconColor || skinSc,
    '--wallet-connect-bg': walletConnectBgColor || skinSc + rgbaZeroPointOne,
    '--wallet-connect-bg-hover': walletConnectBgHoverColor || skinSc + rgbaZeroPointTwo,
    '--wallet-connect-text': walletConnectTextColor || skinSc,
    '--info': infoColor,
    '--danger': dangerColor,
    '--success': successColor,
    '--warning': warningColor,
  };

  const mergeColors = Object.entries(colors).reduce(
    (acc, [name, value]) => ({
      ...acc,
      [`${name}Hover`]: setLightness(value),
      [`${name}Sc`]: getColorMode(getInvert(`${name}Sc`, value)),
      [`${name}Rgb`]: getRgbValue(value),
      [`${name}ScRgb`]: getColorMode(getInvert(`${name}Sc`, value), true),
    }),
    {},
  );

  appendColors({ ...colors, ...mergeColors }, document.head);
};

const createColorSchemaAsian = colorList => {
  const {
    skin: skinColor,
    primary: primaryColor,
    secondary: secondaryColor,
    text: textColor,
    info: infoColor = '#0078D7',
    danger: dangerColor = '#E81123',
    success: successColor = '#3DC526',
    warning: warningColor = '#FFB900',
    ...otherColors
  } = colorList;

  const skinModeColor = getSkinMode(getSkinMode(skinColor));
  const lightPrimaryColor = `#${tinyColor.mix(primaryColor, skinModeColor, 16).toHex()}`;
  const darkPrimaryColor = `#${tinyColor.mix(primaryColor, getSkinMode(skinColor), 49).toHex()}`;

  const colors = {
    '--background': skinColor,
    '--primary': primaryColor,
    '--primary-light': lightPrimaryColor,
    '--primary-dark': darkPrimaryColor,
    '--secondary': secondaryColor || primaryColor,
    '--success': successColor,
    '--danger': dangerColor,
    '--warning': warningColor,
    '--info': infoColor,
    '--dark': dark,
    '--light': light,
  };

  const skinMode = {
    '--skin': getSkinMode(getSkinMode(skinColor)),
    '--skinRgb': getSkinMode(getSkinMode(skinColor), true),
    '--skinSc': getSkinMode(skinColor),
    '--skinScRgb': getSkinMode(skinColor, true),
  };

  const getInvert = (name, value) =>
    otherColors[`${name.slice(2)}Invert`]
      ? getColorMode(otherColors[`${name.slice(2)}Invert`])
      : value;

  const mergeColors = Object.entries(colors).reduce(
    (acc, [name, value]) => ({
      ...acc,
      [`${name}Hover`]: setLightness(value),
      [`${name}Sc`]: getColorMode(getInvert(`${name}Sc`, value)),
      [`${name}Rgb`]: getRgbValue(value),
      [`${name}ScRgb`]: getColorMode(getInvert(`${name}Sc`, value), true),
    }),
    {},
  );

  appendColors({ ...colors, ...skinMode, ...mergeColors }, document.head);
};

const createSchema = currentThemeColors =>
  config.currentSkin === 'asian'
    ? createColorSchemaAsian(currentThemeColors)
    : createSchemaClassic(currentThemeColors);

export default createSchema;
